﻿import { getToken } from './auth'

const VAPID_URL = '/api/v1/push/vapid-public'
const SUBSCRIBE_URL = '/api/v1/push/subscribe'
const SYNC_KEY = 'bpPushSynced:hub'

export function isIos(): boolean {
  const ua = navigator.userAgent || ''
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isMobile(): boolean {
  if (isIos()) return true
  return /Android|webOS|Mobile|Opera Mini|IEMobile/i.test(navigator.userAgent || '')
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function pushSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
}

export function hasPushSynced(): boolean {
  try {
    return sessionStorage.getItem(SYNC_KEY) === '1'
  } catch {
    return false
  }
}

function markPushSynced() {
  try {
    sessionStorage.setItem(SYNC_KEY, '1')
  } catch {
    /* private mode */
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

async function authHeaders(): Promise<HeadersInit> {
  const token = getToken()
  if (!token) throw new Error('Faça login para ativar notificações.')
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export async function registerHubServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    return null
  }
}

function notificationPermission(): NotificationPermission | 'unsupported' {
  try {
    if (typeof Notification === 'undefined') return 'unsupported'
    return Notification.permission
  } catch {
    return 'unsupported'
  }
}

export async function subscribePush(): Promise<void> {
  if (!pushSupported()) {
    throw new Error('Este navegador não suporta notificações push.')
  }
  // Safari em aba normal: não redirecionar (evita piscar / sumir do Hub).
  if (isIos() && !isStandalone()) {
    throw new Error('No iPhone, instale o app (Adicionar à Tela de Início) para ativar notificações.')
  }

  const perm = await Notification.requestPermission()
  if (perm !== 'granted') {
    throw new Error(perm === 'denied' ? 'Notificações bloqueadas no navegador.' : 'Permissão não concedida.')
  }

  await registerHubServiceWorker()
  const reg = await navigator.serviceWorker.ready

  const vapidRes = await fetch(VAPID_URL, { headers: await authHeaders() })
  const vapidJson = (await vapidRes.json()) as {
    success?: boolean
    message?: string
    data?: { public_key?: string }
  }
  const key = vapidJson.data?.public_key
  if (!vapidRes.ok || !key) {
    throw new Error(vapidJson.message || 'Chave VAPID indisponível.')
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
  })
  const json = sub.toJSON()
  const res = await fetch(SUBSCRIBE_URL, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: navigator.userAgent || '',
    }),
  })
  const data = (await res.json()) as { success?: boolean; message?: string }
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Falha ao gravar inscrição.')
  }
  markPushSynced()
}

/** true = mostrar CTA de ativar */
export function shouldShowPushOptin(): boolean {
  if (!isMobile()) return false
  if (!pushSupported() && !(isIos() && !isStandalone())) return false
  const perm = notificationPermission()
  if (perm === 'granted' && hasPushSynced()) return false
  return true
}
